import { useState, useMemo, useEffect } from "react";
import { Search, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import MenuItemCard from "@/components/shared/MenuItemCard";
import ItemDetailModal from "@/components/shared/ItemDetailModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useBranchModal } from "@/contexts/BranchModalContext";
import { usePublicMenu, MenuItem } from "@/hooks/usePublicMenu";
import { useBrand } from "@/contexts/BrandContext";
import { usePublicBranches } from "@/hooks/usePublicBranches";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import CartDrawer from "@/components/shared/CartDrawer";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getClosedBranchMessage } from "@/utils/branchStatus";

const preferredTagOrder = [
  "vegan",
  "vegetarian",
  "gluten_free",
  "halal",
  "spicy",
  "low_spice_level",
  "medium_spice_level",
  "high_spice_level",
];

export default function MenuPage() {
  const { t, language } = useLanguage();
  const { session } = useSessionContext();
  const { openBranchModal } = useBranchModal();
  const { brand, loading: brandLoading } = useBrand();
  const { branches } = usePublicBranches();
  const branchId = session.order.branchId;
  const branchStatus = useMemo(() => {
    const shouldPreferSessionStatus = !!session.order.branchStatusReason || !session.order.isOpen;

    if (branchId && !shouldPreferSessionStatus) {
      const selectedBranch = branches.find((branch) => branch.id === branchId);
      if (selectedBranch) {
        return {
          isOpen: selectedBranch.is_open,
          isBusy: selectedBranch.is_busy,
          reason: session.order.branchStatusReason,
        };
      }
    }

    return {
      isOpen: session.order.isOpen,
      isBusy: session.order.isBusy,
      reason: session.order.branchStatusReason,
    };
  }, [branches, branchId, session.order.branchStatusReason, session.order.isBusy, session.order.isOpen]);

  // When products are available in all branches and no branch is selected,
  // use the first available branch so the menu still loads
  const effectiveBranchId = branchId || (brand.productsAvailableInAllBranches && branches.length > 0 ? branches[0].id : null);

  const { menuItems, categories,tagItems, loading } = usePublicMenu(effectiveBranchId);
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(searchParams.get("category"));
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [showSearch, setShowSearch] = useState(false);

  // Gate: conditionally require branch/delivery selection before viewing menu
  useEffect(() => {
    if (brandLoading) return;
    if (!brand.productsAvailableInAllBranches && !session.order.branchId) {
      // Not shared: must choose delivery/pickup then branch
      openBranchModal();
    } else if (brand.productsAvailableInAllBranches && brand.isDeliveryActive) {
      // Shared + delivery active: need location or branch set
      const hasLocation = !!session.customer?.selectedAddress?.latitude;
      if (!hasLocation && !session.order.branchId) {
        openBranchModal();
      }
    }
    // Shared + pickup only → no gate, menu loads freely (branch required in cart)
  }, [brandLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFilter = (filter: string) => {
    setActiveFilters((prev) =>
      prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]
    );
  };


  const availableTags = useMemo(() => {
  // Collect only tag keys that actually appear on at least one product
  const usedKeys = new Set<string>();
  menuItems.forEach((item) => {
    item.tags.forEach((tag) => usedKeys.add(tag.key));
  });

  // Filter tag_items to only those used, preserving their proper translated names
  return tagItems
    .filter((tag) => usedKeys.has(tag.key))
    .map((tag) => ({
      key: tag.key,
      label:
        language === "ar"
          ? (tag.name.find((n) => n.lang === "ar")?.value ?? tag.key)
          : (tag.name.find((n) => n.lang === "en")?.value ?? tag.key),
    }))
    .sort((a, b) => {
      const ai = preferredTagOrder.indexOf(a.key);
      const bi = preferredTagOrder.indexOf(b.key);
      const na = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
      const nb = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;
      return na !== nb ? na - nb : a.label.localeCompare(b.label);
    });
}, [tagItems, menuItems, language]);

  const filteredItems = useMemo(() => {
    let items = menuItems;
    if (selectedCategory) {
      items = items.filter((item) => item.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          !item.ignoreSearch && (
            item.name.toLowerCase().includes(q) ||
            item.nameAr.toLowerCase().includes(q) ||
            item.description.toLowerCase().includes(q) ||
            item.descriptionAr.toLowerCase().includes(q) ||
            item.tags.some((tag) =>
              tag.key.toLowerCase().includes(q) ||
              tag.name.some((entry) => entry.value.toLowerCase().includes(q))
            )
          )
      );
    }
    if (activeFilters.length > 0) {
      items = items.filter((item) => activeFilters.every((filterKey) => item.tags.some((tag) => tag.key === filterKey)));
    }
    return items;
  }, [selectedCategory, searchQuery, activeFilters, menuItems]);

  const suggestions = useMemo(() => {
    if (searchQuery.trim().length < 2) return [];
    const q = searchQuery.toLowerCase();
      return menuItems
        .filter((item) => !item.ignoreSearch && (item.name.toLowerCase().includes(q) || item.nameAr.toLowerCase().includes(q)))
        .slice(0, 5);
  }, [searchQuery, menuItems]);

  const groupedItems = useMemo(() => {
    if (selectedCategory) {
      const cat = categories.find((c) => c.id === selectedCategory);
      return cat ? [{ category: cat, items: filteredItems }] : [];
    }
    return categories
      .map((cat) => ({
        category: cat,
        items: filteredItems.filter((item) => item.category === cat.id),
      }))
      .filter((group) => group.items.length > 0);
  }, [filteredItems, selectedCategory, categories]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading menu...</div>
        </div>
        <Footer />
      </div>
    );
  }

  const closedBranchMessage = getClosedBranchMessage(
    {
      is_open: branchStatus.isOpen,
      is_busy: branchStatus.isBusy,
      reason: branchStatus.reason,
    },
    language
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 container mx-auto px-4 py-6">
        {(branchId || brand.productsAvailableInAllBranches) && (!branchStatus.isOpen || branchStatus.isBusy) && (
          <Alert className="mb-6 border-warning/40 bg-warning/10 text-foreground">
            <AlertTitle>
              {!branchStatus.isOpen
                ? closedBranchMessage?.title
                : language === "ar"
                  ? "الفرع مشغول حالياً"
                  : "This branch is currently busy"}
            </AlertTitle>
            <AlertDescription>
              {!branchStatus.isOpen
                ? closedBranchMessage?.description
                : language === "ar"
                  ? "قد يستغرق تجهيز الطلب وقتاً أطول من المعتاد لهذا الفرع."
                  : "Orders for this branch may take longer than usual right now."}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-52 shrink-0">
            <nav className="sticky top-20 space-y-1">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`w-full text-start px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  !selectedCategory ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
                }`}
              >
                {t("menu.allCategories")}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`w-full text-start px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === cat.id
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  <span className="me-2">{cat.icon}</span>
                  {language === "ar" ? cat.nameAr : cat.name}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile Categories */}
            <div className="md:hidden flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-none">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  !selectedCategory
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-foreground hover:bg-muted"
                }`}
              >
                {t("menu.allCategories")}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    selectedCategory === cat.id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-foreground hover:bg-muted"
                  }`}
                >
                  {cat.icon} {language === "ar" ? cat.nameAr : cat.name}
                </button>
              ))}
            </div>

            {/* Search & Filters */}
            <div className="mb-6 space-y-3">
              <div className="relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("menu.search")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-10 pe-10"
                  onFocus={() => setShowSearch(true)}
                  onBlur={() => setTimeout(() => setShowSearch(false), 200)}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                {showSearch && suggestions.length > 0 && (
                  <div className="absolute z-20 top-full mt-1 w-full rounded-lg border border-border bg-popover shadow-lg">
                    {suggestions.map((item) => (
                      <button
                        key={item.id}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-start hover:bg-muted transition-colors"
                        onMouseDown={() => {
                          setSelectedItem(item);
                          setSearchQuery("");
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-10 w-10 rounded-md object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {language === "ar" ? item.nameAr : item.name}
                          </p>
                          <p className="text-xs text-muted-foreground">${item.price.toFixed(2)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Tag Filters */}
              {availableTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {availableTags.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => toggleFilter(key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        activeFilters.includes(key)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-foreground hover:bg-muted"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Menu Grid */}
            {groupedItems.map(({ category, items }) => (
              <section key={category.id} className="mb-10">
                <h2 className="font-heading text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <span>{category.icon}</span>
                  {language === "ar" ? category.nameAr : category.name}
                  <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item) => (
                    <MenuItemCard
                      key={item.id}
                      item={item}
                      onClick={setSelectedItem}
                      availableTags={availableTags}
                    />
                  ))}
                </div>
              </section>
            ))}

            {filteredItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                <span className="text-4xl mb-3">🔍</span>
                <p className="text-lg">No items found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Item Detail Modal */}
      {selectedItem && (
        <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} availableTags={availableTags} />
      )}

      <CartDrawer />
      <Footer />
    </div>
  );
}
